import React, { Suspense, useEffect, useRef, useState } from 'react';
// WEB-1988: прелоадер публичного входа вместо кит-прелоадера. Кит тянет antd
// (Typography + theme.useToken()), да ещё и через бочку `components/kit` —
// с ней в граф покупателя приезжала вся библиотека целиком.
import { PublicPreloader } from '@/components/public/PublicPreloader';
import {
  isPreloadSearch,
  resolvePublicHost,
  type ResolvedHostTarget,
} from '@/api/publicResolve';
import { isCustomDomain } from '@/utils/publicHost';
import { isPublicResolveRefusal } from '@/api/publicResolve';
import { getPublicSystemStrings } from '@/components/public/publicFunnelLanguage';
import { PublicSystemPageLazy } from '@/components/public/PublicSystemPageLazy';
// WEB-1989: язык посетителя резолвится ТЕМ ЖЕ правилом, что на самих
// страницах показа (`?lang` → запомненный выбор → язык браузера). Иначе
// резолв собрал бы тело на языке воронки, и посетитель получил бы НЕ тот
// язык за один запрос вместо правильного за два.
import { resolveVisitorLanguages } from '@/utils/visitorLanguage';
import { lazyWithRetry, retryImport } from '@/utils/lazyWithRetry';
// WEB-2054: куски рендерера блоков греются ПО СОСТАВУ воронки — виды блоков
// известны из готового тела показа, приехавшего вместе с резолвом.
import { preloadPublicBlockChunksFor } from '@/player/publicBlockChunkLoaders';
import { collectPublicContentBlockTypes } from '@/player/publicContentBlockTypes';
import { useAppShownSignal } from '@/public/useAppShownSignal';
import { loadShowConfirm } from '@/public/showConfirmLoader';
import { loadStylePack } from '@/themes/stylePacks';
import PublicQuizPage from './PublicQuiz';
// К5 (ревизия 38): плейсхолдеры входа берут высоту вьюпорта из класса
// `.public-page-center` (WEB-973), а не из инлайн-стиля — инлайн не может
// нести пару «100vh, затем 100dvh» (второе значение затирает первое, шрам
// WEB-782), и на мобильном Safari оставался паразитный скролл. Импорт явный,
// хотя тот же файл подключает и PublicQuiz: зависимость этой страницы от
// стилей не должна держаться на чужом импорте.
import '../styles/public-paywall.css';
// WEB-2015: страница пейволла — ОТЛОЖЕННО. Первый экран воронки это квиз, а
// самостоятельный пейволл на клиентском домене — вторая ветка резолва: до неё
// доходит меньшинство показов, а её время выполнения (визард пейволла, кнопка
// покупки, липкая полоса, пиксель покупки, блоки «после оплаты», ≈13 КБ gzip)
// статическим импортом ехало КАЖДОМУ посетителю. Плеер квиза тянет ту же
// страницу лениво с WEB-744 (QuizScreenWizard, переход на COMPLETE) — здесь
// ровно тот же приём и тот же чанк, так что второй копии в сборке не
// появляется. Обёртка `lazyWithRetry`, а не голый `lazy`: путь денежный, и
// выкат новой сборки посреди сессии (хешированный файл на CDN исчез) обязан
// лечиться однократной перезагрузкой за свежим index.html (WEB-1336), а не
// вечным прелоадером в точке оплаты. Держится сторожем сборки:
// `FIRST_SCREEN_DEFERRED_MODULES` в build-guards/publicEntryGuard.ts краснеет,
// если импорт вернут статическим.
// Ключ защёлки задан ЯВНО (свежий взгляд пачки 36, Ф1): денежный путь не
// должен зависеть ни от хеша в имени файла, ни от совпадения имён чанков.
const PublicPaywallPage = lazyWithRetry(
  () => import('./PublicPaywall'),
  'page:public-paywall',
);

// WEB-1349 (подтема 5, п.6): targetId идёт от сервера и подставляется в
// window.location.href как есть — `javascript:`/`data:` тут означает
// исполнение чужого кода на этой странице. Валидация на входе формы
// направления (backend CreateDirectionDto.externalUrl) — первый слой,
// это — второй, независимый от того, не забыли ли валидацию где-то ещё
// (store-fallback URL приходит другим путём — из project.iosStoreUrl/
// androidStoreUrl, не через форму направления).
function isSafeRedirectUrl(value: string): boolean {
  try {
    const url = new URL(value, window.location.href);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Тот же прелоадер, что и на резолве хоста, — и на ожидании ленивого чанка
 * пейволла (WEB-2015). Один вид ожидания на обе паузы: посетитель не должен
 * различать «спрашиваем сервер» и «докачиваем страницу».
 */
function CenteredPreloader() {
  return (
    <div className="public-page-center">
      <PublicPreloader />
    </div>
  );
}

/**
 * Wildcard route that boots the public renderer on a user-owned domain.
 *
 * Flow:
 *  1. If the current hostname is NOT a custom domain, render a 404 — the
 *     normal slug-based routes already handle apex/subdomain hosting.
 *  2. Otherwise, call `GET /public/resolve` to map host+path to a (projectId, slug)
 *     pair, then delegate to PublicQuizPage / PublicPaywallPage with
 *     the resolved identifiers forced through routeOverride props.
 *
 * WEB-263 (render-in-place): smartlink hosts are custom domains. The resolve
 * endpoint selects the sticky direction server-side (using the qb_visitor_id
 * cookie). The FE renders the chosen quiz or paywall IN-PLACE — no redirect.
 */
export default function PublicHostRouter() {
  const [target, setTarget] = useState<ResolvedHostTarget | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // WEB-2024: отказ гейта тарифа (код реестра) — «страница недоступна» на
  // языке покупателя вместо безликого «не найдено».
  const [unavailable, setUnavailable] = useState(false);
  // WEB-3673: признак прогрева SDK (`?preload=1`) — из адресной строки
  // напрямую, как и `?lang` ниже: роутер работает до монтирования страницы
  // показа. Адрес за время жизни роутера не меняется. Форма признака — одна
  // дверь `isPreloadSearch` (та же уходит в запрос резолва).
  const [preload] = useState(() => isPreloadSearch(window.location.search));
  const appShown = useAppShownSignal(preload);
  // Резолв зовётся не больше одного раза за загрузку страницы: без `preload`
  // каждый вызов — засчитанный сервером платный запуск.
  const resolveStartedRef = useRef(false);

  /**
   * ДЕНЕЖНОЕ ПРАВИЛО (WEB-3673, заменяет WEB-2617; решение владельца
   * 21.10.2026). Мобильный SDK грузит страницу заранее в фоновом WebView с
   * `?preload=1`, чтобы после показа не было спиннера. Поэтому резолв уходит
   * СРАЗУ при монтировании и при прогреве: содержимое, кусок пейвола, куски
   * блоков и пак оформления греются до показа. Сервер при `preload=1` запуск
   * НЕ засчитывает — его засчитывает подтверждение показа (эффект ниже),
   * ровно один раз на номер загрузки. Без `preload` всё как раньше: резолв
   * при монтировании, запуск засчитан резолвом, подтверждения нет.
   *
   * Аналитика показа (PAYWALL_VIEW, пиксели, замер скорости) по-прежнему
   * ждёт сигнала показа — внутри самих страниц, этот роутер её не трогает.
   */
  useEffect(() => {
    if (!isCustomDomain()) {
      // Не клиентский домен — резолва не будет вовсе, показа не ждём.
      setNotFound(true);
      setIsLoading(false);
      return;
    }
    if (resolveStartedRef.current) return;
    resolveStartedRef.current = true;
    const host = window.location.hostname;
    const path = window.location.pathname || '/';
    // WEB-1989: `?lang` читаем из адресной строки напрямую — роутер работает
    // до монтирования страницы показа, у него нет её `useSearchParams`.
    const explicitLang = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search || '' : '',
    ).get('lang');
    // WEB-2471 п.2: весь список языков браузера (явный `?lang` и выбор вкладки
    // — по-прежнему один язык).
    const visitorLangs = resolveVisitorLanguages(explicitLang);

    // Async wrapper — matches the pattern used by PublicQuiz/PublicPaywall
    // so that act() flushes properly in tests and React 18 batching applies.
    const resolveAndRender = async () => {
      // WEB-2783: метки посетителя из адресной строки резолв берёт сам
      // (параметр `visitorSearch` по умолчанию), отбор — по белому списку.
      const resolved = await resolvePublicHost(host, path, visitorLangs);
      if (!resolved) {
        setNotFound(true);
        setIsLoading(false);
      } else if (isPublicResolveRefusal(resolved)) {
        if (resolved.code === 'PUBLIC_FUNNEL_UNAVAILABLE') setUnavailable(true);
        else setNotFound(true);
        setIsLoading(false);
      } else if (resolved.targetType === 'redirect') {
        // WEB-395 (re-bounce 2026-07-06): store-fallback (paused/inactive/
        // broken content on a bound domain) and smart-link URL-kind
        // directions both resolve to targetType 'redirect' — navigate the
        // browser to the store/external URL instead of rendering in-place.
        // Previously this branch fell through to the quiz/paywall check and
        // was silently dropped as not-found, so the redirect the backend
        // built correctly never reached the visitor. Intentionally leave
        // isLoading=true (keep the spinner) — the page is navigating away.
        if (isSafeRedirectUrl(resolved.targetId)) {
          window.location.href = resolved.targetId;
        } else {
          setNotFound(true);
          setIsLoading(false);
        }
      } else {
        // WEB-2015: чанк пейволла греется РАНЬШЕ перерисовки — резолв уже
        // сказал, что показ пейволльный, и ждать коммита состояния незачем.
        // Через retryImport, как и сама ленивая страница: ошибка загрузки
        // лечится однократной перезагрузкой (WEB-1336). Результат не ждём —
        // модуль всё равно один, повторный import отдаст тот же промис.
        if (resolved.targetType === 'paywall') {
          void retryImport(
            () => import('./PublicPaywall'),
            'page:public-paywall',
          ).catch(() => {
            // Молчим: тот же импорт повторится при рендере ленивой страницы,
            // и отказ покажет граница падения, а не эта грелка.
          });
        }
        // WEB-2054: та же грелка, что у пейволла выше, но для кусков рендерера
        // блоков. Состав воронки уже на руках (тело показа приехало вместе с
        // резолвом), значит редкие виды могут ехать ПАРАЛЛЕЛЬНО ядру, а не
        // после того, как первый такой блок дойдёт до отрисовки. Тела нет
        // (старый бэк, сборка не удалась) — греть нечего, и это не ошибка:
        // куски приедут при отрисовке, под Suspense.
        preloadPublicBlockChunksFor(
          collectPublicContentBlockTypes(resolved.publicContent),
        );
        // WEB-2645: и пак оформления — тот же приём. Раньше его просила только
        // дверь страницы эффектом ПОСЛЕ первого кадра: данные, потом кусок
        // пака, и лишь затем экран — лишний круг по сети на прелоадере.
        // `loadStylePack` не бросает (отказ — пак по умолчанию), пропускает
        // выдуманный id и пак по умолчанию, а повторный заказ двери отдаёт тот
        // же запрос. Резолв уже прошёл, так что заказ не раньше, чем раньше
        // заказывала дверь.
        // Тело приходит из сети нетипизированным: id берётся из
        // `settings.stylePackId` (та же правда, что у двери), не строка —
        // пака нет. Выдуманный id `loadStylePack` пропускает сам.
        const stylePackId = (
          resolved.publicContent?.settings as
            { stylePackId?: unknown } | undefined
        )?.stylePackId;
        if (typeof stylePackId === 'string') void loadStylePack(stylePackId);
        setTarget(resolved);
        setIsLoading(false);
      }
    };

    resolveAndRender().catch(() => {
      // resolvePublicHost already swallows network errors (returns null).
      // This catch guards against any unexpected synchronous throw.
      setNotFound(true);
      setIsLoading(false);
    });
  }, []);

  /**
   * WEB-3673 — подтверждение показа прогретой страницы (путь денег). Уходит
   * один раз, когда страница реально показана (`useAppShownSignal`: флаг SDK,
   * событие `web2app:shown` или первое касание) и резолв уже дал цель, —
   * ТОЛЬКО при `preload=1` и признаке `showConfirm` в ответе резолва (портал
   * по исключению тарифного гейта его не несёт). Без `preload` не шлётся
   * никогда: запуск уже засчитал резолв, второе подтверждение дало бы
   * двойной счёт. Номер загрузки живёт в sessionStorage — перезагрузка после
   * показа шлёт тот же номер, сервер засчитывает пару один раз. Показ без
   * сигнала и без касания не засчитан — потеря, принятая владельцем.
   */
  useEffect(() => {
    if (!preload || !appShown || !target?.showConfirm) return;
    const { projectId } = target;
    const host = window.location.hostname;
    const path = window.location.pathname || '/';
    void loadShowConfirm().then((mod) => {
      // Кусок не загрузился и после повтора — подтверждение не уходит
      // (запуск не засчитан); страницу не перезагружаем (шрам WEB-2618).
      mod?.confirmShowOnce(projectId, mod.pageLoadId(host, path));
    });
  }, [preload, appShown, target]);

  // Переделка 1 (вес первого экрана): код подтверждения нужен только
  // прогретой странице в приложении — он не едет в первом экране, а
  // грузится лениво ТОЛЬКО при `preload=1`, сразу при монтировании: кусок
  // греется, пока страница в фоне, к показу уже загружен. Без `preload` кусок
  // не грузится никогда.
  useEffect(() => {
    if (preload) void loadShowConfirm();
  }, [preload]);

  if (isLoading) {
    return <CenteredPreloader />;
  }

  if (unavailable) {
    return (
      <PublicSystemPageLazy
        kind="funnel"
        strings={getPublicSystemStrings()}
        data-testid="public-host-unavailable"
      />
    );
  }

  if (notFound || !target) {
    return (
      <PublicSystemPageLazy
        kind="page"
        strings={getPublicSystemStrings()}
        data-testid="public-host-not-found"
      />
    );
  }

  // WEB-1989 шаг 1: готовое тело показа и метка «ответ из копии воркера».
  // Ключи условные — ровно как `paywallOverrideSlug` ниже: тела нет → ключа
  // нет вовсе, и страница остаётся на прежнем втором запросе за контентом.
  const prefetchedProps = {
    ...(target.publicContent ? { prefetched: target.publicContent } : {}),
    // Метка ставится ТОЛЬКО когда ответ действительно пришёл из копии: нет
    // метки — нет и ключа, как у `paywallOverrideSlug`. Так потребитель
    // отличает «воркер сказал: копия» от «признака не приходило вовсе».
    ...(target.servedStale ? { servedStale: true } : {}),
  };

  if (target.targetType === 'quiz') {
    return (
      <PublicQuizPage
        routeOverride={{
          projectId: target.projectId,
          quizSlug: target.slug,
          ...prefetchedProps,
          // WEB-1482: пейволл, выбранный A/B-тестом/маршрутом воронки, —
          // до этой правки он терялся ровно здесь, и после квиза посетитель
          // видел пейволл, подобранный сервером по эвристике. Ключ ставим
          // условно: нет переопределения — нет и ключа, страница квиза
          // остаётся на прежнем запасном пути.
          ...(target.paywallOverrideSlug
            ? { paywallOverrideSlug: target.paywallOverrideSlug }
            : {}),
        }}
      />
    );
  }
  return (
    <Suspense fallback={<CenteredPreloader />}>
      <PublicPaywallPage
        routeOverride={{
          projectId: target.projectId,
          paywallSlug: target.slug,
          ...prefetchedProps,
        }}
      />
    </Suspense>
  );
}
